import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  X,
  Link2Off,
  Link,
  Clock,
  User,
  FileText,
  Tag,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Trash,
  Pencil,
  Calendar,
  BarChart4,
  Filter,
  CheckCircle2,
  Search,
  MoreHorizontal,
  ArrowUpDown,
  RefreshCw,
  ChevronDown,
  Plus,
  Users,
  Boxes,
  PanelRight,
  Menu,
  MessageSquare,
  Upload,
  Download,
  Printer,
  BookOpen,
  Hash,
  Settings,
  LayoutGrid,
  Divide,
  Save,
} from "lucide-react";
import _ from "lodash";
import { getToken, base_url } from "../../config/config";
import { useReactToPrint } from "react-to-print";

// Enhanced Chain Management Components
import InsertProcessModal from "./components/InsertProcessModal";
import ChainPreviewTooltip from "./components/ChainPreviewTooltip";
import ChainConnector from "./components/ChainConnector";
import AddProcessModal from "./components/AddProcessModal";

// Utility function to generate truly unique IDs
const generateUniqueId = (prefix, process, index) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  const processInfo = `${process.id || "no-id"}-${process.anr || "no-anr"}-${
    process.label || "no-label"
  }`;
  return `${prefix}-${processInfo}-${index}-${timestamp}-${random}`;
};

// Utility function imports
const Package = (props) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
};

const CornerRightDown = (props) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="10 15 15 20 20 15" />
      <path d="M4 4h7a4 4 0 0 1 4 4v12" />
    </svg>
  );
};

const Flame = (props) => {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
    </svg>
  );
};
// Enhanced professional color palette with semantic meanings
const processColors = {
  "Materialeingang / Materialausgang mit Kontrolle und Weitergabe an die Produktion incl. Auftragsnummer":
    {
      bg: "#2563eb",
      text: "#ffffff",
      light: "#dbeafe",
      icon: <Boxes className="w-4 h-4" />,
    },
  "Baugruppe montieren": {
    bg: "#0d9488",
    text: "#ffffff",
    light: "#ccfbf1",
    icon: <Users className="w-4 h-4" />,
  },
  "Maschine einrichten": {
    bg: "#0f766e",
    text: "#ffffff",
    light: "#ccfbf1",
    icon: <RefreshCw className="w-4 h-4" />,
  },
  "CNC Fräsen": {
    bg: "#059669",
    text: "#ffffff",
    light: "#d1fae5",
    icon: <ArrowUpDown className="w-4 h-4" />,
  },
  "Konventionell Fräsen": {
    bg: "#65a30d",
    text: "#ffffff",
    light: "#ecfccb",
    icon: <ArrowUpDown className="w-4 h-4" />,
  },
  "CNC Drehen": {
    bg: "#475569",
    text: "#ffffff",
    light: "#f1f5f9",
    icon: <RefreshCw className="w-4 h-4" />,
  },
  "Konventionell Drehen": {
    bg: "#334155",
    text: "#ffffff",
    light: "#f1f5f9",
    icon: <RefreshCw className="w-4 h-4" />,
  },
  Schweißen: {
    bg: "#0284c7",
    text: "#ffffff",
    light: "#e0f2fe",
    icon: <MessageSquare className="w-4 h-4" />,
  },
  Sägen: {
    bg: "#7c2d12",
    text: "#ffffff",
    light: "#ffedd5",
    icon: <Menu className="w-4 h-4" />,
  },
  "Konservieren / Lackieren": {
    bg: "#b91c1c",
    text: "#ffffff",
    light: "#fee2e2",
    icon: <Upload className="w-4 h-4" />,
  },
  "Verpacken / Versenden": {
    bg: "#ca8a04",
    text: "#ffffff",
    light: "#fef3c7",
    icon: <Package className="w-4 h-4" />,
  },
  "Transport innerhalb der Firma": {
    bg: "#4338ca",
    text: "#ffffff",
    light: "#e0e7ff",
    icon: <CornerRightDown className="w-4 h-4" />,
  },
  Qualitätskontrolle: {
    bg: "#15803d",
    text: "#ffffff",
    light: "#dcfce7",
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
  Sonstiges: {
    bg: "#475569",
    text: "#ffffff",
    light: "#f1f5f9",
    icon: <MoreHorizontal className="w-4 h-4" />,
  },
  "Angaben Formulare zu übergeben": {
    bg: "#be185d",
    text: "#ffffff",
    light: "#fce7f3",
    icon: <FileText className="w-4 h-4" />,
  },
  Stoßen: {
    bg: "#0891b2",
    text: "#ffffff",
    light: "#cffafe",
    icon: <ArrowUpDown className="w-4 h-4" />,
  },
  "Normalisieren / Härten": {
    bg: "#1e293b",
    text: "#ffffff",
    light: "#f1f5f9",
    icon: <Flame className="w-4 h-4" />,
  },
};

// Process Popup Component (replacing sidebar)
const ProcessPopup = ({ item, projectData, onClose, onAction, position }) => {
  if (!item) return null;

  const isProcess = item.type === "process";
  const imageUrl = isProcess ? getImageUrl(projectData) : null;
  const timeUtilization =
    isProcess && projectData?.userProjectsExist
      ? calculateTimeUtilization(
          projectData.userProjects,
          item.hours,
          item.label,
        )
      : null;

  const processColor =
    processColors[isProcess ? item.label : "Sonstiges"] ||
    processColors["Sonstiges"];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
        <div
          className="relative transform overflow-hidden rounded-xl bg-white shadow-2xl transition-all sm:w-full sm:max-w-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="border-b border-gray-100">
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <div
                  className="p-2.5 rounded-xl"
                  style={{ backgroundColor: processColor.light }}
                >
                  {processColor.icon}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {item.anr}
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {isProcess ? "Process Details" : "Task Details"}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close panel"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-4 space-y-5 max-h-96 overflow-y-auto">
            {isProcess && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Description
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed p-3 bg-gray-50 rounded-xl">
                  {item.auftragsbezeichnung}
                </p>
              </div>
            )}

            {/* Machine Name */}
            {isProcess && item.machinename && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Machine
                </h3>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <RefreshCw className="w-5 h-5 text-blue-500" />
                  <span className="text-sm text-gray-700 font-medium">
                    {item.machinename}
                  </span>
                </div>
              </div>
            )}

            {imageUrl && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Preview
                </h3>
                <div className="relative group rounded-xl overflow-hidden bg-gray-50">
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <img
                    src={imageUrl}
                    alt={isProcess ? item.auftragsbezeichnung : "Task Image"}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.classList.add("bg-gray-50", "p-6");
                      e.target.src =
                        'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>';
                    }}
                  />
                </div>
              </div>
            )}

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Process Type
              </h3>
              <div
                className="flex items-center gap-3 p-3 rounded-xl"
                style={{ backgroundColor: processColor.light }}
              >
                <div
                  className="p-1.5 rounded-full"
                  style={{ backgroundColor: processColor.bg }}
                >
                  {processColor.icon}
                </div>
                <span className="text-sm text-gray-700 font-medium">
                  {isProcess ? item.label : item.description}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Schedule
              </h3>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Clock className="w-5 h-5 text-gray-500" />
                <div className="text-sm text-gray-700">
                  <div className="font-medium">
                    {isProcess
                      ? `${item.hours} hours planned`
                      : `${item.hours} hours estimated`}
                  </div>
                  <div className="text-gray-500 mt-1">
                    {new Date(item.scheduledDate).toLocaleDateString("de-DE", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">
                Assignee
              </h3>
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="p-2 bg-indigo-100 rounded-full">
                  <User className="w-4 h-4 text-indigo-600" />
                </div>
                <span className="text-sm text-gray-700 font-medium">
                  {isProcess && item.names && item.names.length > 0
                    ? item.names.join(", ")
                    : isProcess
                      ? "Unassigned"
                      : item.assignee || "Unassigned"}
                </span>
              </div>
            </div>

            {isProcess && item.chainId && (
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Chain Information
                </h3>
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <Link className="w-5 h-5 text-blue-500" />
                  <div className="text-sm">
                    <div className="font-medium text-gray-700">
                      Chain ID: {item.chainId.substring(0, 8)}...
                    </div>
                    <div className="text-gray-500 mt-1">
                      Order position: {item.chainOrder + 1}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="border-t border-gray-100 p-5 space-y-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  onAction?.("delete", item._id);
                  onClose();
                }}
                className="flex-1 py-2.5 px-4 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center gap-2"
              >
                <Trash className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ProcessTooltip Component (quick hover info)
const ProcessTooltip = ({ item, projectData, visible, x, y }) => {
  if (!visible || !item) return null;

  const isProcess = item.type === "process";
  const processColor =
    processColors[isProcess ? item.label : "Sonstiges"] ||
    processColors["Sonstiges"];

  return (
    <div
      className="fixed transform -translate-x-1/2 transition-all duration-200 ease-out pointer-events-none z-40"
      style={{
        top: y - 10,
        left: x,
      }}
    >
      <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden w-64 backdrop-blur-sm bg-white/95">
        {/* Tooltip arrow */}
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-b border-r border-gray-100" />

        {/* Content container */}
        <div className="relative p-3">
          {/* Header */}
          <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
            <div
              className="p-1.5 rounded-lg"
              style={{ backgroundColor: processColor.light }}
            >
              {processColor.icon}
            </div>
            <div>
              <span className="font-semibold text-sm text-gray-900">
                {item.anr}
              </span>
              <div className="text-xs text-gray-500">Click for details</div>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-2 mt-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-700">
                {item.hours}h planned
              </span>
            </div>

            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-700 truncate max-w-[180px]">
                {isProcess && item.names && item.names.length > 0
                  ? item.names.join(", ")
                  : isProcess
                    ? "Unassigned"
                    : item.assignee || "Unassigned"}
              </span>
            </div>

            <div className="flex items-start gap-2">
              <Tag className="w-4 h-4 text-gray-400 mt-0.5" />
              <span className="text-xs text-gray-700 truncate max-w-[180px]">
                {isProcess ? item.label : item.description}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// PrintingOptions Component
const PrintingOptions = ({
  isOpen,
  onClose,
  onPrint,
  paperSize,
  setPaperSize,
  printMode,
  setPrintMode,
}) => {
  if (!isOpen) return null;

  const paperSizes = [
    { value: "a1", label: "A1", width: "841mm", height: "594mm" },
    { value: "a2", label: "A2", width: "594mm", height: "420mm" },
    { value: "a3", label: "A3", width: "420mm", height: "297mm" },
    { value: "a4", label: "A4", width: "297mm", height: "210mm" },
  ];

  const printModes = [
    {
      value: "calendar",
      label: "Calendar View",
      icon: <Calendar className="w-4 h-4" />,
    },
    {
      value: "weekly",
      label: "Weekly Machine Plan",
      icon: <LayoutGrid className="w-4 h-4" />,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl p-6 w-96 max-w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Print Options</h2>
          <button
            className="p-2 hover:bg-gray-100 rounded-full"
            onClick={onClose}
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Print Mode
          </label>
          <div className="grid grid-cols-1 gap-3 mb-6">
            {printModes.map((mode) => (
              <button
                key={mode.value}
                onClick={() => setPrintMode(mode.value)}
                className={`flex items-center p-4 border-2 rounded-lg transition-all
                  ${
                    printMode === mode.value
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
              >
                <div className="mr-3">{mode.icon}</div>
                <span className="text-md font-bold">{mode.label}</span>
              </button>
            ))}
          </div>

          <label className="block text-sm font-medium text-gray-700 mb-2">
            Paper Size
          </label>
          <div className="grid grid-cols-2 gap-3">
            {paperSizes.map((size) => (
              <button
                key={size.value}
                onClick={() => setPaperSize(size.value)}
                className={`flex flex-col items-center justify-center p-4 border-2 rounded-lg transition-all
                  ${
                    paperSize === size.value
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
              >
                <span className="text-lg font-bold">{size.label}</span>
                <span className="text-xs text-gray-500 mt-1">
                  {size.width} × {size.height}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onPrint}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium flex items-center hover:bg-blue-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Print Planner
          </button>
        </div>
      </div>
    </div>
  );
};

// Process Card Component
const ProcessCard = ({
  item,
  projectData,
  status,
  onBreakChain,
  onChainAgain,
  onDelete,
  onEdit,
  onHover,
  onLeave,
  onSelect,
  isInGanttView = false,
  showDescriptionInstead = true,
  ...props
}) => {
  const isOverrun = checkTimeOverrun(projectData, item);
  const processColor =
    processColors[item.type === "process" ? item.label : "Sonstiges"] ||
    processColors["Sonstiges"];

  // Check if project data exists
  const hasProjectData = !!(projectData && Object.keys(projectData).length > 0);

  // Display text based on preference (ANR number or description)
  const displayText =
    showDescriptionInstead && item.auftragsbezeichnung
      ? item.auftragsbezeichnung
      : item.anr;

  // Display status - use passed status prop or item status
  const displayStatus = status || item.status;

  // Get profile image URL from projectData
  const profileImageUrl = item?.projectData?.profileImageUrl;
  // Check if this is a broken chain item
  const isBrokenChain = item.chainId && !item.isChained && item.wasChained;

  // Standard calendar view card
  return (
    <div
      {...props}
      data-item-id={item.uniqueId}
      data-chain-id={item.chainId}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.(item, e);
      }}
      // onMouseEnter={(e) => onHover?.(item, e.clientX, e.clientY)}
      onMouseLeave={() => onLeave?.()}
      className={`
        group cursor-pointer rounded-lg relative overflow-visible transition-all duration-300
        hover:shadow-lg hover:translate-y-px transform-gpu min-w-[120px]
        ${isOverrun ? "animate-pulse shadow-red-500/20" : "shadow-sm"}
        ${isBrokenChain ? "border-2 border-dashed border-amber-400 shadow-amber-200/50 animate-pulse" : ""}
        ${props.className || ""}
      `}
      style={{
        ...props.style,
        backgroundColor: isOverrun
          ? "#ef4444"
          : isBrokenChain
            ? "#fef3c7"
            : processColor.bg,
        color: isBrokenChain ? "#92400e" : processColor.text,
      }}
    >
      {/* Background elements */}
      <div className="absolute inset-0 rounded-lg overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-white/20 to-black/20" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_100%_100%,#ffffff33_0%,transparent_50%)]" />
      </div>

      <div className="relative flex flex-col h-full p-2">
        {/* Chain Progress Bar */}
        {item.chainId &&
          item.isChained &&
          typeof item.chainOrder === "number" && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-black/10 rounded-t-lg overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{
                  background: hasProjectData
                    ? "#22c55e"
                    : "linear-gradient(to right, #60a5fa, #3b82f6)",
                  width: `${((item.chainOrder + 1) / (item.totalInChain || 1)) * 100}%`,
                }}
              />
            </div>
          )}

        <div className="flex justify-between items-start gap-1 mt-1">
          <div className="flex items-center gap-2 min-w-0 flex-shrink">
            {/* Enhanced Chain Order Badge */}
            {item.chainId &&
              item.isChained &&
              typeof item.chainOrder === "number" && (
                <div className="flex-shrink-0 relative">
                  {/* Outer glow ring */}
                  <div
                    className="absolute inset-0 rounded-full animate-pulse"
                    style={{
                      backgroundColor: item.chainId
                        ? `hsl(${(item.chainId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) * 137.5) % 360}, 70%, 50%)`
                        : "#3b82f6",
                      filter: "blur(4px)",
                      opacity: 0.4,
                      transform: "scale(1.2)",
                    }}
                  />

                  {/* Main badge */}
                  <div
                    className="relative flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg border-2 border-white"
                    style={{
                      backgroundColor: item.chainId
                        ? `hsl(${(item.chainId.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) * 137.5) % 360}, 70%, 50%)`
                        : "#3b82f6",
                    }}
                    title={`Chain position: ${item.chainOrder + 1}${item.totalInChain ? ` of ${item.totalInChain}` : ""}`}
                  >
                    <span className="relative z-10">{item.chainOrder + 1}</span>

                    {/* Inner shimmer effect */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/30 to-transparent" />
                  </div>

                  {/* Link icon connector */}
                  {item.chainOrder > 0 && (
                    <div className="absolute -left-2 top-1/2 -translate-y-1/2">
                      <Link className="w-3 h-3 text-white/80" strokeWidth={3} />
                    </div>
                  )}
                </div>
              )}

            {/* Profile Image */}
            {profileImageUrl && (
              <img
                src={profileImageUrl}
                alt="Profile"
                className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-white/30 shadow-sm"
              />
            )}
            <div className="font-semibold text-xs leading-tight truncate">
              <div>{item.anr}</div>
              <div>{item?.auftragsbezeichnung}</div>
              <div>{projectData?.projectDetails?.kunden?.bezeichnung}</div>
              {displayStatus && (
                <div className="text-[10px] font-medium mt-0.5 opacity-90">
                  {displayStatus}
                </div>
              )}
            </div>
          </div>
          {/* Action buttons */}
          <div className="absolute -right-1 -top-1 flex gap-1 z-10">
            {isOverrun && (
              <div className="bg-white/10 rounded-full p-1">
                <AlertTriangle className="w-3 h-3 text-yellow-300" />
              </div>
            )}
            {item.chainId && item.isChained == true && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onBreakChain?.(item.chainId);
                }}
                className="opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900/90 hover:bg-gray-900 rounded-full p-1.5 shadow-lg"
                title="Break Chain"
              >
                <Link2Off className="w-3 h-3 text-white" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.(item);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-blue-600/90 hover:bg-blue-700 rounded-full p-1.5 shadow-lg"
              title="Bearbeiten"
            >
              <Pencil className="w-3 h-3 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.(item._id || item.id);
              }}
              className="opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900/90 hover:bg-gray-900 rounded-full p-1.5 shadow-lg"
              title="Delete"
            >
              <Trash className="w-3 h-3 text-white" />
            </button>
          </div>

          {/* Broken Chain Indicator */}
          {item.chainId && item.isChained == false && item.wasChained && (
            <div className="absolute -bottom-1 -right-1 z-10">
              <div className="relative">
                {/* Pulsing glow effect */}
                <div className="absolute inset-0 bg-amber-400 rounded-full blur-md animate-pulse opacity-60" />

                {/* Main badge */}
                <div className="relative flex items-center gap-1 bg-gradient-to-br from-amber-400 to-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg border-2 border-white">
                  <Link2Off className="w-2.5 h-2.5" strokeWidth={3} />
                  <span>Broken</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper functions
const getImageUrl = (projectData) => {
  if (!projectData) return null;
  return projectData.userProjectsExist
    ? projectData.projectDetails?.mainPicture?.s3Url
    : projectData.mainPicture?.s3Url;
};

const calculateTimeUtilization = (userProjects, processHours, label) => {
  if (!userProjects) return null;
  const matchingProjects = userProjects.filter(
    (p) => p.Arbeiten === label && p.EndTime !== "Pending",
  );
  let totalMinutes = 0;
  matchingProjects.forEach((project) => {
    const start = new Date(`2000-01-01 ${project.StartTime}`);
    const end = new Date(`2000-01-01 ${project.EndTime}`);
    totalMinutes += (end - start) / (1000 * 60);
  });
  const totalHours = totalMinutes / 60;
  return {
    hours: totalHours,
    percentage: (totalHours / processHours) * 100,
  };
};

const checkTimeOverrun = (projectData, item) => {
  if (!projectData?.userProjectsExist) return false;
  const matchingProjects = projectData.userProjects.filter(
    (p) => p.Arbeiten === item.label && p.EndTime !== "Pending",
  );
  let totalMinutes = 0;
  matchingProjects.forEach((project) => {
    const start = new Date(`2000-01-01 ${project.StartTime}`);
    const end = new Date(`2000-01-01 ${project.EndTime}`);
    totalMinutes += (end - start) / (1000 * 60);
  });
  return totalMinutes / 60 > item.hours;
};

// InteractivePlanningCalendar Component
const InteractivePlanningCalendar = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [draggingItem, setDraggingItem] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectData, setProjectData] = useState({});
  const [tooltipInfo, setTooltipInfo] = useState({
    visible: false,
    item: null,
    x: 0,
    y: 0,
  });
  const [view, setView] = useState("calendar"); // 'calendar' or 'gantt'

  // Print settings
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [paperSize, setPaperSize] = useState("a3"); // Default to A3
  const [printMode, setPrintMode] = useState("calendar"); // Default to calendar view
  const componentToPrintRef = useRef(null);

  // Display preferences
  const [showDescription, setShowDescription] = useState(false); // Toggle between ANR number and description

  // Enhanced Chain Management State
  const [isChainPanelOpen, setIsChainPanelOpen] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [insertChainId, setInsertChainId] = useState(null);
  const [insertIndex, setInsertIndex] = useState(null);
  const [highlightedChainId, setHighlightedChainId] = useState(null);
  const [chainPreview, setChainPreview] = useState({
    visible: false,
    chain: null,
    x: 0,
    y: 0,
    currentItem: null,
  });

  // Add Process Modal State
  const [isAddProcessModalOpen, setIsAddProcessModalOpen] = useState(false);
  const [addProcessDate, setAddProcessDate] = useState(null);
  const [editingProcess, setEditingProcess] = useState(null); // For edit mode

  // Format month and year for display
  const formatMonthYear = (date) => {
    return date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  };

  // Toggle selection when a card is clicked
  // Ctrl+click to multi-select, regular click to open popup
  const handleSelect = (item, event) => {
    // Check if ctrl key (or cmd on Mac) is pressed for multi-select
    if (event?.ctrlKey || event?.metaKey) {
      // Toggle item in selectedItems array
      setSelectedItems((prev) => {
        const isAlreadySelected = prev.find(
          (i) => i.uniqueId === item.uniqueId,
        );
        if (isAlreadySelected) {
          return prev.filter((i) => i.uniqueId !== item.uniqueId);
        } else {
          return [...prev, item];
        }
      });
    } else {
      // Regular click - open popup and clear multi-selection
      console.log("Selected item:", item);
      setSelectedItem(item);
      // Optionally clear multi-selection when opening popup
      // setSelectedItems([]);
    }
  };

  // Create a new chain from selected items
  const createNewChain = async () => {
    if (selectedItems.length < 2) return;

    const newChainId = `chain-${Date.now()}`;
    console.log(
      "Selected items for new chain:",
      selectedItems.map((i) => {
        return (i.chainOrder, i.date);
      }),
    );
    // Update local state immediately for responsiveness
    const updatedItems = items.map((item) => {
      const selectedIndex = selectedItems.findIndex(
        (i) => i.uniqueId === item.uniqueId,
      );
      if (selectedIndex !== -1) {
        return {
          ...item,
          chainId: newChainId,
          chainOrder: selectedIndex,
          isChained: true,
          hasBeenChained: true,
          wasChained: false,
          scheduledDate: selectedItems[selectedIndex]?.date,
        };
      }
      return item;
    });
    setItems(updatedItems);
    console.log("Creating new chain with ID:", updatedItems);

    // Prepare chain items for API call
    const chainItems = selectedItems.map((item, index) => ({
      ...item,
      chainId: newChainId,
      chainOrder: index,
      isChained: true,
      hasBeenChained: true,
      wasChained: false,
    }));

    // Prepare processes and tasks for API call
    const processesToUpdate = chainItems
      .filter((item) => item.type === "process")
      .map((item) => ({
        ...item,
        instanceId: item.instanceId || item.originalMongoId || item._id,
        scheduledDate: item.date,
      }));

    const tasksToUpdate = chainItems
      .filter((item) => item.type === "task")
      .map((item) => ({
        ...item,
        instanceId: item.instanceId || item.originalMongoId || item._id,
      }));

    try {
      const response = await fetch(`${base_url}/v1/UpdateProjectProcess`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: processesToUpdate,
          tasks: tasksToUpdate,
          projectId: chainItems[0]?.projectId || null,
        }),
      });

      const data = await response.json();

      if (data.status !== 1) {
        console.error("Failed to create new chain:", data.message);
      } else {
        console.log("✅ New chain created successfully:", data);
      }
    } catch (error) {
      console.error("Error creating new chain:", error);
    }

    // Clear selection after chaining
    setSelectedItems([]);
  };

  // Clear multi-selection
  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => componentToPrintRef.current,
    documentTitle: `Project-Planning-${formatMonthYear(currentDate)}`,
    pageStyle: `
      @page {
        size: ${paperSize};
        margin: 10mm;
      }
      @media print {
        html, body {
          width: 100%;
          height: 100%;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .print-container {
          width: 100%;
          height: 100%;
          page-break-inside: avoid;
        }
        
        /* Make sure content is visible */
        .print-container * {
          visibility: visible !important;
          display: block !important;
        }
        
        /* Show descriptions instead of ANR numbers in print */
        .anr-description {
          display: block !important;
        }
        
        /* Paper size specific styles */
        ${
          paperSize === "a1"
            ? `
          .print-container { font-size: 14pt; }
          .calendar-grid { grid-gap: 12px; }
        `
            : paperSize === "a2"
              ? `
          .print-container { font-size: 12pt; }
          .calendar-grid { grid-gap: 10px; }
        `
              : paperSize === "a3"
                ? `
          .print-container { font-size: 11pt; }
          .calendar-grid { grid-gap: 8px; }
        `
                : `
          .print-container { font-size: 10pt; }
          .calendar-grid { grid-gap: 6px; }
        `
        }
      }
    `,
    onBeforeGetContent: () => {
      // Force showing descriptions in print mode
      const originalShowDescription = showDescription;
      setShowDescription(true);

      // Ensure the component is rendered before printing
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
          // Reset to original setting after print preparation
          setShowDescription(originalShowDescription);
        }, 500);
      });
    },
    onAfterPrint: () => {
      setIsPrintDialogOpen(false);
    },
    removeAfterPrint: false,
  });

  // Update hover to show tooltip
  const handleHover = (item, x, y) => {
    if (item) {
      setTooltipInfo({
        visible: true,
        item,
        x,
        y,
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltipInfo((prev) => ({
      ...prev,
      visible: false,
    }));
  };

  const handleBreakChain = async (chainId) => {
    // Find all items in this chain
    const chainItems = items.filter((item) => item.chainId === chainId);
    // Update local state immediately for responsiveness
    const updatedItems = items.map((item) =>
      item.chainId === chainId
        ? { ...item, isChained: false, wasChained: true }
        : item,
    );
    setItems(updatedItems);

    // Prepare processes and tasks for API call
    const processesToUpdate = chainItems
      .filter((item) => item.type === "process")
      .map((item) => ({
        ...item,
        instanceId: item.instanceId || item.originalMongoId || item._id,
        isChained: false,
        wasChained: true,
        scheduledDate: item.date,
      }));

    const tasksToUpdate = chainItems
      .filter((item) => item.type === "task")
      .map((item) => ({
        ...item,
        instanceId: item.instanceId || item.originalMongoId || item._id,
        isChained: false,
        wasChained: true,
      }));

    try {
      const response = await fetch(`${base_url}/v1/UpdateProjectProcess`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: processesToUpdate,
          tasks: tasksToUpdate,
          projectId: chainItems[0]?.projectId || null,
        }),
      });

      const data = await response.json();

      if (data.status !== 1) {
        console.error("Failed to update chain break:", data.message);
        // Optionally revert the local state on failure
      } else {
        console.log("✅ Chain broken successfully:", data);
      }
    } catch (error) {
      console.error("Error breaking chain:", error);
      // Optionally revert the local state on failure
    }
  };

  const createChain = async (chainId) => {
    const chainItems = items.filter((item) => item.chainId === chainId);
    // Update local state immediately for responsiveness
    const updatedItems = items.map((item) =>
      item.chainId === chainId
        ? { ...item, isChained: true, wasChained: true }
        : item,
    );
    setItems(updatedItems);

    console.log("Creating chain for items:", chainItems);

    // Prepare processes and tasks for API call
    const processesToUpdate = chainItems
      .filter((item) => item.type === "process")
      .map((item) => ({
        ...item,
        isChained: true,
        instanceId: item.instanceId || item.originalMongoId || item._id,
        scheduledDate: item.date,
      }));

    const tasksToUpdate = chainItems
      .filter((item) => item.type === "task")
      .map((item) => ({
        ...item,
        instanceId: item.instanceId || item.originalMongoId || item._id,
      }));

    try {
      const response = await fetch(`${base_url}/v1/UpdateProjectProcess`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: processesToUpdate,
          tasks: tasksToUpdate,
          projectId: chainItems[0]?.projectId || null,
        }),
      });

      const data = await response.json();

      if (data.status !== 1) {
        console.error("Failed to create chain:", data.message);
      } else {
        console.log("✅ Chain created successfully:", data);
      }
    } catch (error) {
      console.error("Error creating chain:", error);
    }
  };

  const handleAction = (action, value) => {
    if (action === "delete") {
      handleDelete(value);
    } else if (action === "breakChain") {
      handleBreakChain(value);
    } else if (action === "chainAgain") {
      createChain(value);
    }
  };

  // ========== ENHANCED CHAIN MANAGEMENT HANDLERS ==========

  // Handle inserting a process into a chain
  const handleInsertIntoChain = (chainId, afterIndex) => {
    setInsertChainId(chainId);
    setInsertIndex(afterIndex);
    setIsInsertModalOpen(true);
  };

  // Insert selected process into chain
  const handleInsertProcess = async (selectedProcess) => {
    if (!insertChainId || insertIndex === null) return;

    // Get chain items
    const chainItems = items
      .filter((item) => item.chainId === insertChainId && item.isChained)
      .sort((a, b) => a.chainOrder - b.chainOrder);

    // Update chain orders for items after insert point
    const updatedItems = items.map((item) => {
      if (item.chainId === insertChainId && item.isChained) {
        if (item.chainOrder > insertIndex) {
          return { ...item, chainOrder: item.chainOrder + 1 };
        }
      }
      return item;
    });

    // Add the new process to the chain
    const newProcess = {
      ...selectedProcess,
      chainId: insertChainId,
      chainOrder: insertIndex + 1,
      isChained: true,
      hasBeenChained: true,
      wasChained: false,
    };

    // Find the date for the new process (interpolate between before and after)
    const beforeItem = chainItems[insertIndex];
    const afterItem = chainItems[insertIndex + 1];

    if (beforeItem && beforeItem.date) {
      const beforeDate = new Date(beforeItem.date);
      const nextWorkday = getNextWorkday(beforeDate);
      newProcess.date = nextWorkday.toLocaleDateString("en-CA");
    }

    // Update local state
    setItems([...updatedItems, newProcess]);

    // API call to update
    try {
      const processesToUpdate = [...updatedItems, newProcess]
        .filter((item) => item.type === "process")
        .map((item) => ({
          ...item,
          instanceId: item.instanceId || item.originalMongoId || item._id,
          scheduledDate: item.date,
        }));

      await fetch(`${base_url}/v1/UpdateProjectProcess`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: processesToUpdate,
          projectId: selectedProcess?.projectId || null,
        }),
      });

      console.log("✅ Process inserted into chain successfully");
    } catch (error) {
      console.error("Error inserting process into chain:", error);
    }

    // Close modal
    setIsInsertModalOpen(false);
    setInsertChainId(null);
    setInsertIndex(null);
  };

  // Highlight chain on hover
  const handleHighlightChain = (chainId) => {
    setHighlightedChainId(chainId);
  };

  // Enhanced hover handler for chained items
  const handleChainHover = (item, x, y) => {
    if (item && item.chainId && item.isChained) {
      // Get all items in this chain
      const chainItems = items.filter(
        (i) => i.chainId === item.chainId && i.isChained,
      );

      setChainPreview({
        visible: true,
        chain: {
          id: item.chainId,
          items: chainItems,
        },
        x,
        y,
        currentItem: item,
      });
    }

    // Also call regular hover for tooltip
    handleHover(item, x, y);
  };

  // Clear chain preview
  const handleChainHoverLeave = () => {
    setChainPreview({
      visible: false,
      chain: null,
      x: 0,
      y: 0,
      currentItem: null,
    });
    handleMouseLeave();
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSaveUpdates = async (settingItems) => {
    setIsSaving(true);
    try {
      // Separate processes and tasks from items
      const processesToUpdate = settingItems
        .filter((item) => item.type === "process")
        .map((item) => ({
          ...item,
          instanceId: item.instanceId || item.originalMongoId || item._id,
          scheduledDate: item.date,
        }));

      const tasksToUpdate = settingItems
        .filter((item) => item.type === "task")
        .map((item) => ({
          ...item,
          instanceId: item.instanceId || item.originalMongoId || item._id,
        }));

      const response = await fetch(`${base_url}/v1/UpdateProjectProcess`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          processes: processesToUpdate,
          tasks: tasksToUpdate,
        }),
      });

      const data = await response.json();

      if (data.status !== 1) {
        console.error("Failed to save updates:", data.message);
      }
    } catch (error) {
      console.error("Error saving updates:", error);
      alert("Error saving updates: " + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const goToPreviousMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const goToNextMonth = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const goToCurrentMonth = () => {
    setCurrentDate(new Date());
  };

  useEffect(() => {
    const fetchProjectData = async (anr) => {
      try {
        const response = await fetch(
          `${base_url}/v1/getUserProjectbyANR2/${anr}`,
        );
        const data = await response.json();
        console.log(`📊 Fetching project data for ANR ${anr}:`, data);
        if (data.status === 1) {
          setProjectData((prev) => ({
            ...prev,
            [anr]: data.data,
          }));
          console.log(`✅ Project data stored for ANR ${anr}`, data.data);
        } else {
          console.log(
            `⚠️ No project data found for ANR ${anr}, status:`,
            data.status,
          );
        }
      } catch (error) {
        console.error(`Error fetching project data for ANR ${anr}:`, error);
      }
    };

    const uniqueANRs = [
      ...new Set(
        items.filter((item) => item.type === "process").map((item) => item.anr),
      ),
    ];
    console.log("🔍 Unique ANRs to fetch:", uniqueANRs);
    uniqueANRs.forEach(fetchProjectData);
  }, [items]);

  const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  const getPreviousWorkday = (date) => {
    const prevDate = new Date(date);
    prevDate.setDate(prevDate.getDate() - 1);
    while (isWeekend(prevDate)) {
      prevDate.setDate(prevDate.getDate() - 1);
    }
    return prevDate;
  };

  const getNextWorkday = (date) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + 1);
    while (isWeekend(nextDate)) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    return nextDate;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const paddingDays = firstDay === 0 ? 6 : firstDay - 1;

    const previousMonth = new Date(year, month, 0);
    const prevMonthDays = Array.from({ length: paddingDays }, (_, i) => {
      return new Date(
        year,
        month - 1,
        previousMonth.getDate() - paddingDays + i + 1,
      );
    });

    const currentMonthDays = Array.from({ length: daysInMonth }, (_, i) => {
      return new Date(year, month, i + 1);
    });

    const totalDays = prevMonthDays.length + currentMonthDays.length;
    const remainingDays = 42 - totalDays;

    const nextMonthDays = Array.from({ length: remainingDays }, (_, i) => {
      return new Date(year, month + 1, i + 1);
    });

    return [...prevMonthDays, ...currentMonthDays, ...nextMonthDays];
  };

  const handleDragStart = (item) => {
    setDraggingItem(item);
  };

  // Open Add Process Modal for a specific date
  const handleOpenAddProcessModal = (date) => {
    const dateKey = date.toLocaleDateString("en-CA");
    setAddProcessDate(dateKey);
    setEditingProcess(null); // Ensure not in edit mode
    setIsAddProcessModalOpen(true);
  };

  // Open Edit Process Modal with existing data
  const handleEditProcess = (process) => {
    console.log("Editing process:", process);
    setAddProcessDate(process.scheduledDate || process.date);
    setEditingProcess(process);
    setIsAddProcessModalOpen(true);
  };

  // Handle adding a new process
  const handleAddProcess = (newProcess) => {
    const processToAdd = {
      ...newProcess,
      uniqueId: newProcess.uniqueId || `new-${Date.now()}`,
      type: "process",
      anr: newProcess.anr?.anr || newProcess.anr,
      label: newProcess.processType?.label || "New Process",
      hours: parseFloat(newProcess.hours) || 0,
      names: newProcess.assignee ? [newProcess.assignee.name] : [],
      machine: newProcess.machine?.name || null,
    };
    setItems((prev) => [...prev, processToAdd]);
  };

  // Handle updating an existing process
  const handleUpdateProcess = (updatedProcess) => {
    setItems((prev) =>
      prev.map((item) =>
        item._id === updatedProcess._id
          ? {
              ...item,
              ...updatedProcess,
              uniqueId: item.uniqueId, // Preserve original uniqueId
              date: updatedProcess.scheduledDate,
            }
          : item,
      ),
    );
  };

  const distributeChainDates = (dropDate, chainItems, dropItemOrder) => {
    const parseScheduled = (it) => {
      return it && it.scheduledDate ? new Date(it.scheduledDate) : null;
    };

    const refDate = parseScheduled(chainItems[dropItemOrder]) || new Date(); // drop date reference

    const startOfDay = (d) => {
      const dt = new Date(d);
      dt.setHours(0, 0, 0, 0);
      return dt;
    };

    // function to calcuate off sets
    const workdayDifference = (a, b) => {
      const from = startOfDay(a);
      const to = startOfDay(b);

      if (from.getTime() === to.getTime()) return 0;

      let count = 0;
      if (from < to) {
        const cur = new Date(from);
        while (cur < to) {
          cur.setDate(cur.getDate() + 1);
          const day = cur.getDay();
          if (day !== 0 && day !== 6) count++;
        }
        return count;
      } else {
        const cur = new Date(from);
        while (cur > to) {
          count++;
          cur.setDate(cur.getDate() - 1);
          while (cur.getDay() === 0 || cur.getDay() === 6) {
            cur.setDate(cur.getDate() - 1);
          }
        }
        return -count;
      }
    };

    const offsets = chainItems.map((it) => {
      const sd = parseScheduled(it) || new Date();
      return workdayDifference(refDate, sd);
    });

    const addWorkdaysForward = (start, n) => {
      let d = new Date(start);
      for (let i = 0; i < n; i++) {
        d = getNextWorkday(d);
      }
      return d;
    };

    // Helper to add n working-day steps backward
    const addWorkdaysBackward = (start, n) => {
      let d = new Date(start);
      for (let i = 0; i < n; i++) {
        d = getPreviousWorkday(d);
      }
      return d;
    };

    const base = new Date(dropDate);
    const dates = offsets.map((offset) => {
      if (offset === 0) {
        // ensure dropped date is a workday
        if (isWeekend(base)) return getNextWorkday(base);
        return new Date(base);
      }
      if (offset > 0) {
        return addWorkdaysForward(base, offset);
      }
      // offset < 0
      return addWorkdaysBackward(base, Math.abs(offset));
    });

    console.log(dates, offsets);

    return dates;
  };

  const dayItemsMap = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (item.scheduledDate) {
        const dateKey = new Date(item.scheduledDate).toLocaleDateString(
          "en-CA",
        );
        if (!map.has(dateKey)) {
          map.set(dateKey, []);
        }
        map.get(dateKey).push(item);
      }
    });
    return map;
  }, [items]);

  const handleDrop = useCallback(
    async (date) => {
      if (!draggingItem) return;
      const dropDate = new Date(date);
      if (isWeekend(dropDate)) return;

      // handling chained items
      if (draggingItem.chainId && draggingItem.isChained === true) {
        const chainItems = items
          .filter(
            (item) => item.chainId === draggingItem.chainId && item.isChained,
          )
          .sort((a, b) => a.chainOrder - b.chainOrder);
        console.log(
          "Dropping chained items:",
          chainItems,
          draggingItem.uniqueId,
        );
        const dropItemOrder = chainItems.findIndex(
          (item) => item.uniqueId === draggingItem.uniqueId,
        );
        const dates = distributeChainDates(dropDate, chainItems, dropItemOrder);
        const updatedItems = items.map((item) => {
          const chainIndex = chainItems.findIndex(
            (chainItem) => chainItem.uniqueId === item.uniqueId,
          );
          if (chainIndex !== -1) {
            return {
              ...item,
              date: dates[chainIndex].toLocaleDateString("en-CA"),
              scheduledDate: dates[chainIndex].toLocaleDateString("en-CA"),
            };
          }
          return item;
        });
        setItems(updatedItems);
        try {
          await handleSaveUpdates(updatedItems);
        } catch (err) {
          console.error("Error saving updates after drop (chained):", err);
        }
      } else {
        // single item drag-and-drop
        const updatedItem = items.map((item) =>
          item.uniqueId === draggingItem.uniqueId
            ? {
                ...item,
                date: dropDate.toLocaleDateString("en-CA"),
                scheduledDate: dropDate.toLocaleDateString("en-CA"),
              }
            : item,
        );
        setItems(updatedItem);
        try {
          await handleSaveUpdates(updatedItem);
        } catch (err) {
          console.error("Error saving updates after drop (single):", err);
        }
      }
      setDraggingItem(null);
    },
    [items, draggingItem, handleSaveUpdates],
  );

  const handleDelete = async (itemId) => {
    try {
      const response = await fetch(`${base_url}/v1/deleteProcess/${itemId}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete process");
      }

      // Find the item by original ID for deletion, but remove by uniqueId
      const itemToDelete = items.find((item) => item._id === itemId);
      if (itemToDelete) {
        setItems(
          items.filter((item) => item.uniqueId !== itemToDelete.uniqueId),
        );
        if (selectedItem?.uniqueId === itemToDelete.uniqueId) {
          setSelectedItem(null);
        }
      }
    } catch (error) {
      console.error("Error deleting process:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [processesResponse, tasksResponse] = await Promise.all([
          fetch(`${base_url}/v1/GetProjectPlanning`),
          fetch(`${base_url}/v1/GetAllTasks`),
        ]);

        const processesData = await processesResponse.json();
        const tasksData = await tasksResponse.json();

        if (processesData.status === 1 && tasksData.status === 1) {
          const formattedProcesses = processesData.data.processes.map(
            (process, index) => {
              // Generate truly unique identifier for each process instance
              const uniqueId =
                process._id || generateUniqueId("process", process, index);

              return {
                ...process,
                type: "process",
                date: process.scheduledDate?.split("T")[0],
                hasBeenChained: false,
                wasChained: false,
                assignee: process.names[0],
                order: process.chainOrder,
                // Ensure unique identifier for each process instance
                uniqueId: uniqueId,
                // Keep original _id and id for API calls
                originalId: process.id,
                originalMongoId: process._id,
              };
            },
          );

          const formattedTasks = tasksData.data.map((task, index) => {
            // Generate truly unique identifier for each task instance
            const uniqueId =
              task._id ||
              generateUniqueId(
                "task",
                { id: task.id, anr: task.anr, label: task.description },
                index,
              );

            return {
              ...task,
              type: "task",
              date: task.date?.split("T")[0],
              hasBeenChained: false,
              wasChained: false,
              label: task.description,
              order: null,
              // Ensure unique identifier for each task instance
              uniqueId: uniqueId,
              // Keep original _id and id for API calls
              originalId: task.id,
              originalMongoId: task._id,
            };
          });

          let allItems = [...formattedProcesses, ...formattedTasks];

          // Calculate total in chain for each item
          const chainGroups = {};
          allItems.forEach((item) => {
            if (item.chainId && item.isChained) {
              if (!chainGroups[item.chainId]) {
                chainGroups[item.chainId] = [];
              }
              chainGroups[item.chainId].push(item);
            }
          });

          // Add totalInChain to each item
          allItems = allItems.map((item) => {
            if (item.chainId && item.isChained && chainGroups[item.chainId]) {
              return {
                ...item,
                totalInChain: chainGroups[item.chainId].length,
              };
            }
            return item;
          });

          // Enhanced debugging for identical processes
          const identicalProcessGroups = formattedProcesses.reduce(
            (acc, process) => {
              const key = `${process.originalId}-${process.anr}-${process.label}`;
              if (!acc[key]) {
                acc[key] = [];
              }
              acc[key].push(process);
              return acc;
            },
            {},
          );

          const identicalProcesses = Object.entries(identicalProcessGroups)
            .filter(([key, processes]) => processes.length > 1)
            .reduce((acc, [key, processes]) => {
              acc[key] = {
                count: processes.length,
                uniqueIds: processes.map((p) => p.uniqueId),
                details: processes.map((p) => ({
                  uniqueId: p.uniqueId,
                  originalId: p.originalId,
                  originalMongoId: p.originalMongoId,
                  anr: p.anr,
                  label: p.label,
                  date: p.date,
                })),
              };
              return acc;
            }, {});

          console.log("📊 InteractivePlanner: Processed data", {
            totalProcesses: formattedProcesses.length,
            totalTasks: formattedTasks.length,
            totalItems: allItems.length,
            identicalProcessGroups: Object.keys(identicalProcesses).length,
            identicalProcesses: identicalProcesses,
            sampleProcesses: formattedProcesses.slice(0, 3).map((p) => ({
              id: p.originalId,
              _id: p.originalMongoId,
              uniqueId: p.uniqueId,
              label: p.label,
              anr: p.anr,
              date: p.date,
            })),
          });

          // Final verification: Check that all uniqueIds are actually unique
          const uniqueIdCounts = allItems.reduce((acc, item) => {
            acc[item.uniqueId] = (acc[item.uniqueId] || 0) + 1;
            return acc;
          }, {});

          const duplicateUniqueIds = Object.entries(uniqueIdCounts).filter(
            ([id, count]) => count > 1,
          );

          if (duplicateUniqueIds.length > 0) {
            console.error(
              "🚨 CRITICAL: Found duplicate uniqueIds!",
              duplicateUniqueIds,
            );
          } else {
            console.log("✅ All uniqueIds are truly unique!");
          }

          setItems(allItems);
        }
      } catch (err) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading calendar data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex-1 p-6 transition-all duration-300">
        {/* Header section with view toggle and print options */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={createNewChain}
                disabled={selectedItems.length < 2}
                className={`px-5 py-2.5 rounded-lg flex items-center gap-3 font-medium transition-all
                  ${
                    selectedItems.length >= 2
                      ? "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
              >
                <Link className="w-5 h-5" />
                Chain Selected ({selectedItems.length})
              </button>
              {selectedItems.length > 0 && (
                <button
                  onClick={clearSelection}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-all"
                >
                  Clear Selection
                </button>
              )}
              {selectedItems.length === 0 && (
                <span className="text-xs text-gray-500 italic">
                  Ctrl+Click to select multiple items
                </span>
              )}

              <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-white">
                <button
                  onClick={() => setView("calendar")}
                  className={`px-4 py-2 flex items-center gap-2 text-sm font-medium transition-colors ${
                    view === "calendar"
                      ? "bg-blue-50 text-blue-600"
                      : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Calendar
                </button>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <button
                onClick={goToPreviousMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-6 h-6 text-gray-600" />
              </button>

              <div className="flex flex-col items-center min-w-[200px]">
                <span className="text-xl font-semibold text-gray-900">
                  {formatMonthYear(currentDate)}
                </span>
                <button
                  onClick={goToCurrentMonth}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-1"
                >
                  Today
                </button>
              </div>

              <button
                onClick={goToNextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Next month"
              >
                <ChevronRight className="w-6 h-6 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Display and Print Options */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleSaveUpdates(items)}
                disabled={isSaving}
                className={`px-4 py-2 rounded-lg shadow-sm text-sm font-medium flex items-center gap-2 transition-all ${
                  isSaving
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save Updates"}
              </button>
              <button
                onClick={() => setIsPrintDialogOpen(true)}
                className="px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 text-gray-700 text-sm font-medium flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print Planner
              </button>
            </div>
          </div>
        </div>

        <div className="print-container" ref={componentToPrintRef}>
          {/* Machine List for Printing - Only visible in print */}
          <div className="hidden print:block mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Machines</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(processColors).map(([processName, color]) => (
                <div
                  key={processName}
                  className="flex items-center p-3 rounded-lg border border-gray-200"
                >
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: color.bg }}
                  ></div>
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {processName}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 p-6 overflow-hidden">
            {/* Professional Background Pattern */}
            <div className="absolute inset-0 opacity-5 pointer-events-none">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "radial-gradient(circle at 2px 2px, rgba(59, 130, 246, 0.3) 1px, transparent 0)",
                  backgroundSize: "40px 40px",
                }}
              />
            </div>

            {/* Calendar Grid Wrapper with Chain Connectors */}
            <div className="relative">
              {/* Chain Visual Connectors */}
              <ChainConnector
                items={items.filter((item) => item.date)}
                currentDate={currentDate}
                getDaysInMonth={getDaysInMonth}
              />

              <div className="relative grid grid-cols-7 gap-3 calendar-grid">
                {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((day) => (
                  <div
                    key={day}
                    className={`text-center font-bold p-3 rounded-xl transition-all shadow-sm
                      ${
                        day === "Sa" || day === "So"
                          ? "bg-gradient-to-br from-gray-100 to-gray-200 text-gray-500"
                          : "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-blue-200"
                      }`}
                  >
                    {day}
                  </div>
                ))}

                {getDaysInMonth(currentDate).map((date) => {
                  const isWeekendDay = isWeekend(date);
                  const dateKey = date.toLocaleDateString("en-CA");
                  const dayItems = !isWeekendDay
                    ? dayItemsMap.get(dateKey) || []
                    : [];

                  // Debug logging for specific dates with multiple items
                  if (dayItems.length > 1) {
                    const identicalOnDay = dayItems.reduce((acc, item) => {
                      const key = `${item.originalId}-${item.anr}-${item.label}`;
                      if (!acc[key]) {
                        acc[key] = [];
                      }
                      acc[key].push(item);
                      return acc;
                    }, {});

                    const hasIdenticalProcesses = Object.values(
                      identicalOnDay,
                    ).some((group) => group.length > 1);
                  }
                  const itemHeight =
                    dayItems.length > 0
                      ? `${Math.max(28, Math.min(36, 120 / dayItems.length))}px`
                      : "36px";
                  const isToday =
                    date.toDateString() === new Date().toDateString();

                  return (
                    <div
                      key={date.toISOString()}
                      className={`
                        relative min-h-[180px] rounded-xl transition-all duration-300 overflow-visible
                        ${
                          isToday
                            ? "border-2 border-blue-500 ring-2 ring-blue-200 shadow-lg shadow-blue-200/50 bg-gradient-to-br from-blue-50 to-white"
                            : "border-2 border-gray-200 shadow-md hover:shadow-xl"
                        }
                        ${
                          isWeekendDay
                            ? "bg-gradient-to-br from-gray-100 to-gray-50 cursor-not-allowed opacity-75"
                            : date.getMonth() === currentDate.getMonth()
                              ? "bg-white hover:border-blue-300 hover:-translate-y-0.5"
                              : "bg-gradient-to-br from-gray-50 to-gray-100 opacity-60"
                        }
                      `}
                      onDragOver={(e) => !isWeekendDay && e.preventDefault()}
                      onDrop={() => !isWeekendDay && handleDrop(date)}
                    >
                      {/* Date Header with Professional Badge */}
                      <div className="relative">
                        <div
                          className={`
                            inline-flex items-center justify-center min-w-[32px] h-8 px-2 m-2 rounded-lg font-bold text-sm
                            ${
                              date.getMonth() === currentDate.getMonth()
                                ? isWeekendDay
                                  ? "bg-gray-200 text-gray-500"
                                  : isToday
                                    ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md shadow-blue-300/50"
                                    : "bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 hover:from-blue-100 hover:to-blue-200"
                                : "bg-gray-100 text-gray-400"
                            }
                            transition-all duration-200
                          `}
                        >
                          {date.getDate()}
                        </div>

                        {/* Today Indicator */}
                        {isToday && (
                          <div className="absolute -top-1 -right-1">
                            <div className="relative">
                              <div className="w-3 h-3 bg-blue-500 rounded-full animate-ping absolute" />
                              <div className="w-3 h-3 bg-blue-500 rounded-full relative" />
                            </div>
                          </div>
                        )}

                        {/* Add Process Button */}
                        {!isWeekendDay &&
                          date.getMonth() === currentDate.getMonth() && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenAddProcessModal(date);
                              }}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-blue-300 hover:bg-blue-500 text-white flex items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 opacity-60 hover:opacity-100"
                              title="Prozess hinzufügen"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                      </div>

                      <div className="px-1 flex flex-wrap gap-1 relative">
                        {dayItems.map((item, itemIndex) => {
                          // Calculate width based on hours (8 hours = 100%)
                          const MAX_DAILY_HOURS = 8;
                          const hours =
                            parseFloat(item.hours) || MAX_DAILY_HOURS;
                          const widthPercent = Math.max(
                            20,
                            Math.min(100, (hours / MAX_DAILY_HOURS) * 100),
                          );

                          // Determine if project data exists (project exists in API)
                          const hasProjectData = projectData[item.anr];
                          const backgroundColor = hasProjectData
                            ? "#22c55e" // green color for projects that exist
                            : item.type === "process"
                              ? processColors[item.label]?.bg
                              : processColors["Sonstiges"].bg;

                          // Debug logging
                          if (itemIndex === 0 && dayItems.length > 0) {
                            console.log(
                              `🎨 Rendering item for date ${dateKey}:`,
                              {
                                anr: item.anr,
                                hasProjectData: !!hasProjectData,
                                backgroundColor,
                                projectDataKeys: Object.keys(projectData),
                              },
                            );
                          }

                          return (
                            <ProcessCard
                              key={item.uniqueId}
                              item={item}
                              projectData={projectData[item.anr]}
                              status={
                                hasProjectData ? "In Bearbeitung" : item.status
                              }
                              draggable
                              showDescriptionInstead={showDescription}
                              onHover={handleChainHover}
                              onLeave={handleChainHoverLeave}
                              onSelect={handleSelect}
                              onDragStart={() => handleDragStart(item)}
                              onBreakChain={handleBreakChain}
                              onChainAgain={createChain}
                              onDelete={handleDelete}
                              onEdit={handleEditProcess}
                              style={{
                                width: `calc(${widthPercent}% - 4px)`,
                                minWidth: "80px",
                                backgroundColor: backgroundColor,
                              }}
                              className={`
                                transform transition-all duration-200 flex-shrink-0
                                ${
                                  selectedItems.find(
                                    (i) => i.uniqueId === item.uniqueId,
                                  )
                                    ? "ring-2 ring-blue-500 scale-105 shadow-lg"
                                    : ""
                                }
                                ${item.chainId && item.isChained ? "border-2 border-blue-400 shadow-lg shadow-blue-200/50" : "shadow-md"}
                                ${highlightedChainId === item.chainId ? "ring-4 ring-yellow-400 z-20 scale-105 shadow-2xl" : ""}
                                hover:scale-102 hover:shadow-xl
                              `}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tooltip */}
      <ProcessTooltip
        item={tooltipInfo.item}
        projectData={
          tooltipInfo.item ? projectData[tooltipInfo.item.anr] : null
        }
        visible={tooltipInfo.visible}
        x={tooltipInfo.x}
        y={tooltipInfo.y}
      />

      {/* Popup Dialog for selected item */}
      {selectedItem && (
        <ProcessPopup
          item={selectedItem}
          projectData={projectData[selectedItem.anr]}
          onClose={() => setSelectedItem(null)}
          onAction={handleAction}
        />
      )}

      {/* Print Options Dialog */}
      <PrintingOptions
        isOpen={isPrintDialogOpen}
        onClose={() => setIsPrintDialogOpen(false)}
        onPrint={handlePrint}
        paperSize={paperSize}
        setPaperSize={setPaperSize}
      />

      {/* Chain Preview Tooltip */}
      <ChainPreviewTooltip
        chain={chainPreview.chain}
        visible={chainPreview.visible}
        x={chainPreview.x}
        y={chainPreview.y}
        currentItem={chainPreview.currentItem}
      />

      {/* Insert Process Modal */}
      <InsertProcessModal
        isOpen={isInsertModalOpen}
        onClose={() => {
          setIsInsertModalOpen(false);
          setInsertChainId(null);
          setInsertIndex(null);
        }}
        onInsert={handleInsertProcess}
        chainItems={items.filter(
          (i) => i.chainId === insertChainId && i.isChained,
        )}
        insertIndex={insertIndex}
        availableProcesses={items}
        projectData={projectData}
      />

      {/* Add/Edit Process Modal */}
      <AddProcessModal
        isOpen={isAddProcessModalOpen}
        onClose={() => {
          setIsAddProcessModalOpen(false);
          setAddProcessDate(null);
          setEditingProcess(null);
        }}
        onAdd={handleAddProcess}
        onUpdate={handleUpdateProcess}
        selectedDate={addProcessDate}
        editingProcess={editingProcess}
      />
    </div>
  );
};

export default InteractivePlanningCalendar;
